import { useState, useEffect } from "react";
import { useNavigate }          from "react-router-dom";
import { apiClient }            from "../../api/apiServices";

// ─────────────────────────────────────────────────────────────
// STYLING PASS — the previous version was functionally solid but
// visually flat: every goal card looked identical regardless of
// progress, the hero card was a single static block with no sense of
// portfolio composition, and there was no near-completion moment that
// felt different from a goal sitting at 5%.
//
// CHANGES:
//  - GoalCard: progress bar now carries more visual weight (taller,
//    glowing near completion), a near-complete state (>=85%) gets a
//    distinct treatment (pulsing accent, "Almost there" tag) instead
//    of looking identical to a barely-started goal. Auto-save status
//    now surfaces directly on the card (small chip) when enabled —
//    previously invisible anywhere in the UI despite being tracked
//    server-side.
//  - Hero card: added a lightweight composition strip (active vs
//    completed goal count as a visual split, not just a sentence) so
//    "Total Saved" reads as a portfolio summary, not just a number.
// ─────────────────────────────────────────────────────────────

function ProgressBar({ percent, glow }) {
  return (
    <div className="h-2.5 bg-surface-hover rounded-full overflow-hidden relative">
      <div
        className="h-full rounded-full transition-all duration-700 relative"
        style={{
          width: `${Math.min(percent, 100)}%`,
          background: "linear-gradient(90deg, var(--color-brand), var(--color-brand-pop))",
          boxShadow: glow ? "0 0 12px var(--color-brand-ring)" : "none",
        }}
      >
        {glow && (
          <div
            className="absolute inset-0 rounded-full animate-pulse"
            style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)" }}
          />
        )}
      </div>
    </div>
  );
}

// FIXED — goal.daysRemaining was being trusted directly from the API
// with no validation, and was showing wrong/stale numbers (confirmed:
// not computed from endDate correctly server-side, or not recomputed
// on each fetch). Computed here instead, from endDate, which both
// pages already have and is unambiguous — removes the dependency on
// the backend getting this one derived field right. Uses calendar-day
// rounding (not raw ms/86400000, which would undercount by a day in
// many cases depending on time-of-day on either end) and floors at 0
// rather than showing a negative count for an overdue/matured goal.
function daysRemainingFrom(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const now = new Date();
  // Compare at the day level, not the millisecond level — strips time
  // components so "endDate is tomorrow at 00:00" and "now is today at
  // 23:59" still correctly reads as 1 day remaining, not 0.
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((endDay - nowDay) / 86400000);
  return Math.max(diffDays, 0);
}

function AutoSaveChip({ autoSave }) {
  if (!autoSave?.enabled) return null;
  // FIXED — surfaces a real data gap: autoSave.enabled can be true
  // while autoSave.amount is 0 (confirmed from real goal data) — a
  // goal that LOOKS automated but isn't actually moving any money.
  // Distinguishing this from a properly-configured auto-save avoids
  // silently implying progress is happening when it isn't.
  const isConfigured = Number(autoSave.amount) > 0;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{
        background: isConfigured ? "var(--color-info-light)" : "var(--color-warning-light)",
        color:      isConfigured ? "var(--color-info-text)"  : "var(--color-warning-text)",
      }}
    >
      <i className={isConfigured ? "ri-repeat-line" : "ri-error-warning-line"} />
      {isConfigured ? `Auto-saves ₦${Number(autoSave.amount).toLocaleString()} ${autoSave.frequency}` : "Auto-save on, no amount set"}
    </span>
  );
}

function GoalCard({ goal, onClick }) {
  const pct = goal.progressPercent ?? 0;
  const isCompleted = goal.status === "completed";
  const isActive    = goal.status === "active";
  const isNearDone  = isActive && pct >= 85;
  // FIXED — was goal.daysRemaining (unreliable, see file header note).
  const daysLeft = daysRemainingFrom(goal.endDate);

  const badgeStyle = isCompleted
    ? { background: "var(--color-success-light)", color: "var(--color-success-text)" }
    : isNearDone
    ? { background: "var(--color-brand-light)",    color: "var(--color-brand-text)" }
    : isActive
    ? { background: "var(--color-info-light)",    color: "var(--color-info-text)" }
    : { background: "var(--color-surface-hover)", color: "var(--color-text-muted)" };

  const iconChipStyle = isCompleted
    ? {
        background: "var(--color-success-light)",
        boxShadow:  "inset 0 0 0 1px var(--color-success-ring)",
      }
    : {
        background: "var(--color-brand-light)",
        boxShadow:  "inset 0 0 0 1px var(--color-brand-ring)",
      };

  const iconColor = isCompleted ? "var(--color-success)" : "var(--color-brand)";

  return (
    <button
      onClick={onClick}
      className="w-full bg-surface border rounded-2xl p-4 text-left transition-all shadow-card relative overflow-hidden"
      style={{ borderColor: isNearDone ? "var(--color-brand-border)" : "var(--color-border)" }}
    >
      {/* FIXED — near-completion goals get a subtle corner glow,
          giving the "almost there" moment real visual weight instead
          of looking identical to a 5%-progress card. */}
      {isNearDone && (
        <div
          className="absolute -top-8 -right-8 w-24 h-24 rounded-full pointer-events-none"
          style={{ background: "var(--color-brand-light)", opacity: 0.6, filter: "blur(8px)" }}
        />
      )}

      <div className="flex items-start justify-between mb-3 relative">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={iconChipStyle}
          >
            <i
              className={goal.type === "fixed" ? "ri-lock-2-line" : "ri-safe-2-line"}
              style={{ color: iconColor }}
            />
          </div>
          <div>
            <p className="text-primary font-semibold text-sm">{goal.title}</p>
            <p className="text-muted text-xs capitalize">{goal.type} · {daysLeft != null ? `${daysLeft}d left` : "—"}</p>
          </div>
        </div>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 flex items-center gap-1"
          style={badgeStyle}
        >
          {isNearDone && <i className="ri-fire-line" />}
          {isNearDone ? "Almost there" : goal.status}
        </span>
      </div>

      <div className="flex justify-between items-end mb-1.5 relative">
        <span className="text-primary font-bold">₦{Number(goal.currentAmount).toLocaleString()}</span>
        <span className="text-muted text-xs">of ₦{Number(goal.targetAmount).toLocaleString()}</span>
      </div>

      <ProgressBar percent={pct} glow={isNearDone} />

      <div className="flex items-center justify-between mt-1.5 relative">
        <p className="text-xs font-semibold" style={{ color: "var(--color-brand-text)" }}>
          {pct}% saved
        </p>
        <AutoSaveChip autoSave={goal.autoSave} />
      </div>
    </button>
  );
}

export default function SavingsPage() {
  const navigate = useNavigate();
  const [goals, setGoals]      = useState([]);
  const [totalSaved, setTotal] = useState(0);
  const [loading, setLoading]  = useState(true);

  useEffect(() => {
    apiClient.get("/mtrade/savings")
      .then(r => {
        if (r.data.status) {
          setGoals(r.data.data);
          setTotal(r.data.totalSaved || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeCount    = goals.filter(g => g.status === "active").length;
  const completedCount = goals.filter(g => g.status === "completed").length;

  return (
    <div className="min-h-screen bg-bg pb-28">
      {/* Top bar */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/home")}
              className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center"
            >
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <h1 className="text-primary font-bold text-lg font-display">Savings</h1>
          </div>
          <button
            onClick={() => navigate("/savings/new")}
            className="flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-on-brand text-xs font-bold px-3 py-2 rounded-xl transition-colors"
          >
            <i className="ri-add-line" />New Goal
          </button>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-3xl mx-auto space-y-5">
        {/* Hero card — gradient + sheen layer */}
        <div
          className="rounded-2xl overflow-hidden relative"
          style={{ boxShadow: "var(--gradient-hero-shadow)" }}
        >
          <div
            className="px-5 py-5 text-center relative"
            style={{ background: "var(--gradient-hero)" }}
          >
            {/* Sheen — radial highlight in dark mode, `none` in light */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "var(--gradient-hero-sheen)" }}
            />
            <p
              className="text-xs mb-1 relative"
              style={{ color: "var(--gradient-hero-text-faint)" }}
            >
              Total Saved
            </p>
            <p className="text-white font-bold text-3xl font-display relative">
              ₦{Number(totalSaved).toLocaleString()}
            </p>

            {/* FIXED — portfolio composition strip, replaces the
                previous plain sentence. Reads as a summary of WHERE
                the goals stand, not just a count. */}
            {goals.length > 0 && (
              <div className="flex items-center justify-center gap-4 mt-3 relative">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: "#fff" }} />
                  <span className="text-xs" style={{ color: "var(--gradient-hero-text-muted)" }}>{activeCount} active</span>
                </div>
                {completedCount > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,255,255,0.5)" }} />
                    <span className="text-xs" style={{ color: "var(--gradient-hero-text-muted)" }}>{completedCount} completed</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Goal list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="h-28 bg-skeleton rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-12">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                background: "var(--color-brand-light)",
                boxShadow:  "inset 0 0 0 1px var(--color-brand-ring)",
              }}
            >
              <i className="ri-safe-2-line text-2xl" style={{ color: "var(--color-brand)" }} />
            </div>
            <p className="text-primary font-bold text-sm">No savings goals yet</p>
            <p className="text-muted text-xs mt-1 mb-4">Start saving towards something today.</p>
            <button
              onClick={() => navigate("/savings/new")}
              className="bg-brand hover:bg-brand-hover text-on-brand font-bold text-sm px-5 py-3 rounded-2xl transition-colors"
            >
              Create Your First Goal
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {goals.map(g => (
              <GoalCard key={g._id} goal={g} onClick={() => navigate(`/savings/${g._id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}